import { ERoles } from '@constants';
import { IUser } from '@sdk/contracts';
import { useQuery } from '@tanstack/react-query';
import { useUnleashContext } from '@unleash/proxy-client-react';
import { parseCookies } from '@utils';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCurrentStudentTutorDetail } from '../tutoring-groups';

import { useMutateProfile } from '.';

import { useSocket } from '@/hooks';
import { sdk } from '@/services';
import { checkUserRole } from '@/utils';

const UserService = sdk.getService('UserService');

export const useProfile = () => {
  const { isWorking, updateProfile: mutateProfile } = useMutateProfile();
  const { tutor } = useCurrentStudentTutorDetail();
  const updateContext = useUnleashContext();
  const navigateTo = useNavigate();
  const { token } = parseCookies();
  const { connect } = useSocket();
  const prevUserIdRef = useRef<string | undefined>();
  const {
    isLoading,
    data: profile,
    refetch
  } = useQuery({
    queryKey: ['profile', token],
    queryFn: () => UserService.getMe(),
    enabled: !!token,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false
  });

  useEffect(() => {
    if (profile) {
      const currentId = profile?._id;
      if (!currentId) return;
      if (prevUserIdRef.current !== currentId) {
        updateContext({ userId: currentId });
      }
      prevUserIdRef.current = currentId;
      connect(profile);
    }
  }, [profile, updateContext, connect]);

  const roleCheck = checkUserRole(profile);

  const moreInfo = () => UserService.moreInfo();

  const updateProfile = async (updatedUser: Partial<IUser>, onSuccess?: () => void) => {
    mutateProfile(updatedUser, {
      onSuccess: () => onSuccess?.()
    });
  };

  const isOnboardingCompleted =
    profile?.contract?.signatureRequestId === 'fake' ||
    profile?.role !== ERoles.STUDENT ||
    (!import.meta.env.PROD && !!profile?.contract?.isContractSigned) ||
    (!!profile?.contract?.isContractSigned && !!profile?.contract?.contractFile);

  const { isStudent } = roleCheck;

  useEffect(() => {
    !isLoading && !isOnboardingCompleted && navigateTo('/onboarding');
  }, [isStudent, isOnboardingCompleted, isLoading, navigateTo]);

  return {
    isLoading,
    isWorking,
    profile,
    refetch,
    updateProfile,
    roleCheck,
    moreInfo,
    tutor,
    isOnboardingCompleted
  };
};
